import { useCallback, useEffect, useState } from 'react';
import { fetchMe, fetchStatus, runSelfTest } from './api';
import type { Me, Status } from './api';
import { brand } from './brand';

/**
 * Is the whole chain working, right now?
 *
 * This exists because every failure in this system so far has been invisible
 * from the outside — a recorder crash-looping inside a sandbox, a socket that
 * lost a startup race, a key rotation the far end never picked up — and each was
 * only findable by reading journals over SSH. The point is to answer "is it
 * working?" BEFORE a meeting rather than after it.
 */
export function Diagnostics() {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    fetchMe().then(setMe).catch(() => setMe(null));
  }, []);

  const load = useCallback(() => {
    fetchStatus()
      .then((s) => {
        setStatus(s);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    load();
    // Slowly. The transcriber check CONNECTS, and the far end is socket-activated
    // — so each refresh starts a unit on somebody's desktop. Often enough to be
    // live, rare enough not to be a nuisance.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const selfTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await runSelfTest();
      setTestResult(r.message);
      setTimeout(load, 3000);
    } catch (e) {
      setTestResult(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  }, [load]);

  const p = status?.pipeline;

  // The frame supplies the bar, the nav and the account menu (AppShell); this
  // page is its own content only.
  return (
    <>
      {error && <p className="mx-error">{error}</p>}
      {!status && !error && <p className="mx-muted">Checking…</p>}

      {status && (
        <>
          {me?.authenticated && (
            <p className="mx-muted mx-whoami">
              Signed in as <strong>{me.username || me.email || me.name}</strong> —{' '}
              {me.op ? (
                <span className="mx-yes">operator</span>
              ) : (
                <>
                  <span className="mx-no">not an operator</span>. Add exactly this
                  identity to <code>services.meetnix.operators</code>; a short name
                  matches nobody.
                </>
              )}
            </p>
          )}

          <ul className="mx-checks">
            {status.checks.map((c) => (
              <li key={c.name} className={c.ok ? 'ok' : 'bad'}>
                <span className="mx-dot" aria-hidden="true" />
                <span className="mx-check-name">{c.name}</span>
                <span className="mx-check-detail">
                  {c.detail}
                  {!c.ok && c.hint && <em className="mx-check-hint">{c.hint}</em>}
                </span>
              </li>
            ))}
          </ul>

          {p && (
            <section className="mx-pipeline">
              <h2>Pipeline</h2>
              <p className="mx-muted">
                {p.total} finished meeting{p.total === 1 ? '' : 's'} ·{' '}
                {p.awaitingTranscript} awaiting transcript · {p.awaitingSummary} awaiting
                summary · {p.awaitingDelivery} awaiting email · {p.complete} complete
              </p>
              <p className="mx-muted">
                A number here that only ever grows is a stalled stage — that is what this
                row is for.
              </p>
            </section>
          )}

          <section className="mx-pipeline">
            <h2>Self-test</h2>
            <p className="mx-muted">
              Creates a synthetic meeting with a canned transcript and lets the real
              pipeline summarise and email it. Proves OpenRouter, the mail path and the
              rendering against live credentials. It does <strong>not</strong> fake the
              recorder or the transcriber — those are proven by holding an actual meeting.
            </p>
            <button
              className="mx-button"
              onClick={() => void selfTest()}
              disabled={testing || !me?.op}
              title={me?.op ? '' : 'Operators only'}
            >
              {testing ? 'Running…' : 'Run self-test'}
            </button>
            {testResult && <p className="mx-muted">{testResult}</p>}
          </section>
        </>
      )}

      <SupportFooter />
      <SourceOffer />
    </>
  );
}

/**
 * AGPL §13: anyone interacting with a modified version of this software over a
 * network must be offered its Corresponding Source.
 *
 * Two values, and they are independent. The REVISION is the one that answers
 * "which code is this instance actually running?" — the question every
 * investigation on this page starts with — and it is available even when the
 * operator named no URL, because it comes from the flake rather than from a
 * string somebody maintains. The URL is the offer proper, and is the operator's
 * to set: if they patched the code, the source they owe their users is theirs
 * and not this project's, which is why there is no default here to fall back
 * on.
 *
 * Shown short, linked long. A full revision is unreadable and nobody types one
 * out; twelve characters identify a commit unambiguously in any repository this
 * size, and the title attribute carries the whole thing for copying.
 */
function SourceOffer() {
  const { sourceURL, sourceRev } = brand();
  if (!sourceURL && !sourceRev) return null;

  const short = sourceRev ? sourceRev.slice(0, 12) : null;
  const rev = short && (
    <code className="mx-source-rev" title={sourceRev ?? undefined}>
      {short}
    </code>
  );

  return (
    <p className="mx-muted mx-source">
      {sourceURL ? (
        <>
          Source:{' '}
          <a className="mx-support-link" href={sourceURL} target="_blank" rel="noreferrer">
            {sourceURL}
          </a>
          {rev && <> at {rev}</>}
        </>
      ) : (
        <>Running revision {rev}. This instance publishes no source location.</>
      )}
    </p>
  );
}

/**
 * Where to go when this page says something is broken.
 *
 * Rendered outside the `status &&` block on purpose: the moment you most want
 * the help link is the one where the status endpoint itself did not answer, and
 * a link that disappears exactly when the system is unwell is not a help link.
 *
 * Omitted entirely when the operator named no page — see brand.Support().
 */
function SupportFooter() {
  const support = brand().supportURL;
  if (!support) return null;
  return (
    <p className="mx-muted">
      <a className="mx-support-link" href={support} target="_blank" rel="noreferrer">
        Help and troubleshooting
      </a>
    </p>
  );
}
